import React from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Box, Typography, Link } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useApiGet } from '../../../utils/httpClientTs';
import { getLocalChapterDetail } from '../../../endpoints/chaptersTs';
import type { LocalChapterDetailResponse } from '../../../types/chapters';

export interface LCSubPageLayoutProps {
  title: string;
  children: (detail: LocalChapterDetailResponse) => React.ReactNode;
}

const LCSubPageLayout: React.FC<LCSubPageLayoutProps> = ({
  title,
  children,
}) => {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading } = useApiGet(
    ['lc-chapters-detail', id],
    () => getLocalChapterDetail(id!),
    { enabled: !!id, retry: 1 }
  );

  if (!id) {
    return (
      <Typography color="error">Invalid route. Missing chapter ID.</Typography>
    );
  }

  if (isLoading || !data) {
    return <Typography color="text.secondary">Loading...</Typography>;
  }

  if (!data.local_chapter_access) {
    return (
      <Box sx={{ py: 3 }}>
        <Typography color="text.secondary">
          You do not have access to this chapter.
        </Typography>
        <Link
          component={RouterLink}
          to={`/local-chapters/${id}?tab=more`}
          sx={{ mt: 2, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          <ArrowBackIcon fontSize="small" />
          Back to {data.name}
        </Link>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to={`/local-chapters/${id}?tab=more`}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          color="inherit"
          underline="hover"
        >
          <ArrowBackIcon fontSize="small" />
          Back to {data.name}
        </Link>
        <Typography variant="h5" component="h2" sx={{ mt: 1 }}>
          {data.name} {title}
        </Typography>
      </Box>
      {children(data)}
    </Box>
  );
};

export default LCSubPageLayout;
